<script setup>
// A configuração da ferramenta "Pedir atendimento humano": o diálogo que abre do card dela em
// Ferramentas. Até 22/09/2026 isto era a aba Atendimento humano. A aba saiu porque o switch da
// tool morava em Ferramentas e o que ela liga morava aqui — dois lugares para uma coisa só
// (decisão do Murilo: tudo na ferramenta).
//
// O que NÃO mora aqui é o switch. Ligar e desligar é do card, e só a plataforma mexe nele: o
// backend recusa mudar `ativa` a quem não é super-admin. Este save manda o bloco com o `ativa`
// que já estava gravado, por spread, e por isso passa para o cliente.
//
// Três decisões que vieram da aba:
//
//   1. tudo nasce LIGADO — calar quando o atendente responde, voltar quando a conversa é
//      resolvida, a tool de pedir humano. Um padrão que protege e não aparece é um padrão
//      que ninguém sabe que tem; quando alguém desligar, vai ser de propósito. A leitura é
//      `!== false` e não `|| true`: a diferença é entre "nunca configurado" e "desligado de
//      propósito", e `|| true` religaria o que alguém desligou;
//   2. o save manda SÓ a sub-chave `handoff`, como o follow-up. O merge do servidor é raso,
//      então o que este formulário não gerencia — a palavra-chave, de fase seguinte — é
//      preservado por spread, e não apagado por omissão;
//   3. `abrir_conversa` desligado ganha aviso âmbar na hora: sem ele o pedido de humano só
//      roteia, e ninguém vê a conversa na fila. É uma escolha legítima (integração que abre
//      por conta própria), mas tem de ser escolha.
//
// O último grupo vale com a ferramenta DESLIGADA: é sobre qualquer atendente que entra na
// conversa, não sobre o agente pedir um. Fica aqui porque é aqui que se procura, e o próprio
// grupo diz, por escrito, que independe do switch.
import { ref, computed, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { mensagemDoErro } from '../erros';
import {
  limparGatilho,
  gatilhosDoTexto,
  gatilhosValidos as listarValidos,
  gatilhosRepetidos as listarRepetidos,
  temGatilhoVazio,
  quandoTexto as fraseDoQuando,
} from '../helpers/gatilhos';
import powerAgentsApi from 'dashboard/api/powerAgents';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  agent: { type: Object, required: true },
  // Os times da conta vêm do card: ele já os busca para dizer "Vai para Consultores", e uma
  // segunda busca aqui seria a mesma lista em duas versões do mesmo instante.
  teams: { type: Array, default: () => [] },
});

const emit = defineEmits(['saved']);

const { t } = useI18n();
const ctx = inject('powerAgentContext');
// O diálogo é teleportado para o body e fica FORA do `<fieldset disabled>` do SectionShell.
// Sem este, o papel `agent` abriria o diálogo e editaria campos que o servidor vai recusar.
const somenteLeitura = inject(
  'powerAgentSomenteLeitura',
  computed(() => false)
);

// O piso do escalonamento, igual ao da API e ao do runtime: abaixo disso ele dispara em
// cima da conversa. O campo impede antes de o Save ir; a API recusa de qualquer jeito.
const MINUTOS_MINIMOS = 5;
// O padrão do runtime quando ninguém escreveu (`handoff.py`): meia hora.
const ESCALONAMENTO_PADRAO = 30;
// A dormência nasce com as mesmas 24 h que a pausa por humano já tem sem ela (`HUMAN_HOLD`
// em `state.py`): ligar no padrão não muda comportamento nenhum, só passa o relógio para a
// mão de quem configura.
const DORMENCIA_PADRAO = 1440;
// A API fala minutos e vai continuar falando. A TELA não devia: "1440" não se lê como "um
// dia", e obrigar quem configura a dividir por 60 é transformar uma escolha de operação em
// aritmética. O campo guarda valor + unidade e converte na hora de salvar.
const UNIDADES = { minutos: 1, horas: 60, dias: 1440 };

// A maior unidade que divide certo, com uma exceção: UM dia é mostrado como 24 horas. O
// padrão da dormência é exatamente esse caso, e "24 horas" é como ele é falado em todo
// lugar (na dica aqui do lado, na `HUMAN_HOLD` do runtime) — mostrar "1 dia" faria a tela
// discordar do próprio texto. De dois dias em diante, "2 dias" ganha de "48 horas".
// 90 min fica em minutos: 1,5 h num campo de inteiro seria mentira.
function unidadeDe(minutos) {
  if (minutos >= 2 * UNIDADES.dias && minutos % UNIDADES.dias === 0)
    return 'dias';
  if (minutos > 0 && minutos % UNIDADES.horas === 0) return 'horas';
  return 'minutos';
}

const saving = ref(false);

const bloco = props.agent.config?.handoff || {};
const dormencia = bloco.volta?.dormencia || {};
const tool = bloco.tool_solicitar_humano || {};
const roteamento = tool.roteamento || {};
const escalonamento = tool.escalonamento || {};
const resumo = tool.resumo_ao_encaminhar || {};

const silenciarQuandoHumano = ref(
  bloco.silenciar_quando_humano_responde !== false
);
const voltaAoResolver = ref(bloco.volta?.ao_resolver !== false);
// Desligada por padrão, como o escalonamento e pelo mesmo motivo: quanto tempo a conversa
// fica com a pessoa é decisão de operação, e herdar um prazo que ninguém escolheu seria o
// bot voltando a falar no meio de um atendimento humano.
const dormenciaAtiva = ref(Boolean(dormencia.ativa));
const dormenciaSalva = dormencia.apos_minutos ?? DORMENCIA_PADRAO;
const dormenciaUnidade = ref(unidadeDe(dormenciaSalva));
const dormenciaValor = ref(dormenciaSalva / UNIDADES[dormenciaUnidade.value]);
// Só leitura, e do agente e não do formulário: o switch é do card.
const ligada = computed(
  () => props.agent.config?.handoff?.tool_solicitar_humano?.ativa !== false
);
const silenciarAoSolicitar = ref(tool.silenciar_ao_solicitar !== false);
const abrirConversa = ref(tool.abrir_conversa !== false);
// Uma linha por categoria, com o nome e o time JUNTOS. Antes eram dois controles: um campo
// de texto com vírgulas aqui e uma lista de mapeamento mais abaixo — quem configurava tinha
// de declarar a categoria num lugar para só então poder rotear no outro, e a lista de baixo
// não dizia de onde os nomes vinham. É a mesma decisão; agora é um controle só.
const categoriasLinhas = ref(
  (tool.categorias || []).map(nome => ({
    nome,
    time:
      roteamento.por_categoria?.[nome] == null
        ? ''
        : String(roteamento.por_categoria[nome]),
  }))
);

function adicionarCategoria() {
  categoriasLinhas.value.push({ nome: '', time: '' });
}

function removerCategoria(indice) {
  categoriasLinhas.value.splice(indice, 1);
}
const roteamentoPadrao = ref(
  roteamento.padrao ? String(roteamento.padrao) : ''
);
const mensagemAoCliente = ref(tool.mensagem_ao_cliente || '');
// QUANDO pedir humano: uma LISTA de gatilhos, uma situação por linha, no molde das
// categorias (pedido do Murilo, 22/09/2026). Quem decide continua sendo o agente: a lista
// vai para a descrição da tool — onde o modelo decide, e não o prompt do agente, onde uma
// condição de uso some no meio de 64 KB — e ele a lê a cada turno.
//
// O runtime só conhece texto (`quando`, a frase do meio de `_descricao_solicitar_humano` em
// runtime/turn.py). Esta tela é quem escreve a lista, e ela vira "Use quando: a; b; c." — o
// parser mora em `helpers/gatilhos.js`, compartilhado com o diálogo do funil.
//
// Vazio no banco = o critério padrão do runtime (`_QUANDO_PADRAO`). Ele aparece aqui como
// três gatilhos editáveis, porque um padrão que não aparece é um padrão que ninguém sabe que
// tem. Se voltam intactos, ou se a lista fica vazia, o save manda `null` e o runtime segue
// com a frase dele.
const QUANDO_MAX = 1000; // o teto da API: a frase viaja em TODA requisição ao modelo
const GATILHO_MAX = 200;
const gatilhosPadrao = [
  t('POWER_AGENTS.HANDOFF.WHEN_DEFAULT_ASKED'),
  t('POWER_AGENTS.HANDOFF.WHEN_DEFAULT_SCOPE'),
  t('POWER_AGENTS.HANDOFF.WHEN_DEFAULT_SITUATION'),
];
const quandoGravado = tool.quando || null;
const gatilhosLinhas = ref(
  gatilhosDoTexto(quandoGravado, gatilhosPadrao).map(texto => ({ texto }))
);
// A lista como ela abriu, já limpa: é a régua de "ninguém mexeu".
const gatilhosIniciais = gatilhosLinhas.value
  .map(linha => limparGatilho(linha.texto))
  .filter(Boolean);

function adicionarGatilho() {
  gatilhosLinhas.value.push({ texto: '' });
}

function removerGatilho(indice) {
  gatilhosLinhas.value.splice(indice, 1);
}
// O escalonamento nasce DESLIGADO, ao contrário do resto: escalar para um time é uma
// decisão de operação (qual time? depois de quanto?) que ninguém deve herdar sem escolher.
const escalonamentoAtivo = ref(Boolean(escalonamento.ativo));
const escalonamentoMinutos = ref(
  escalonamento.apos_minutos ?? ESCALONAMENTO_PADRAO
);
const escalonamentoPara = ref(
  escalonamento.para ? String(escalonamento.para) : ''
);
// O resumo nasce DESLIGADO, como o escalonamento e pelo mesmo motivo que o runtime usa:
// é uma chamada ao modelo a mais por encaminhamento. Capacidade que custa e que aparece
// para quem atende é escolha de quem configura, não herança da frota publicada.
const resumoAtivo = ref(Boolean(resumo.ativo));
// O acréscimo de quem configura ao prompt da plataforma — nunca o substituto dele: os três
// tópicos são contrato com quem LÊ a anotação. Vazio vira `null` pelo mesmo motivo do
// `quando`: string vazia é `valor_invalido` no runtime e vira nota em TODO turno.
const resumoInstrucao = ref(resumo.instrucao || '');

// A lista limpa, e ela é a ÚNICA fonte das duas coisas que vão para a API (os nomes e as
// rotas). Derivar as duas do mesmo lugar é o que impede a rota de sobreviver ao nome —
// com duas passagens independentes, uma linha repetida mandava o nome de uma e a rota de
// outra. Vazio e repetido caem aqui porque a API recusa os dois.
const linhasValidas = computed(() => {
  const vistos = new Set();
  return categoriasLinhas.value
    .map(linha => ({ nome: linha.nome.trim(), time: linha.time }))
    .filter(linha => {
      if (!linha.nome || vistos.has(linha.nome)) return false;
      vistos.add(linha.nome);
      return true;
    });
});
const categorias = computed(() => linhasValidas.value.map(linha => linha.nome));

// Mesma regra das categorias: vazio e repetido não vão, e a tela diz isso em vez de calar.
const textosDosGatilhos = computed(() =>
  gatilhosLinhas.value.map(linha => linha.texto)
);
const gatilhosValidos = computed(() => listarValidos(textosDosGatilhos.value));
const quandoTexto = computed(() =>
  fraseDoQuando(gatilhosValidos.value, {
    iniciais: gatilhosIniciais,
    gravado: quandoGravado,
    padrao: gatilhosPadrao,
  })
);
const quandoLongo = computed(
  () => (quandoTexto.value || '').length > QUANDO_MAX
);
const gatilhoVazio = computed(() => temGatilhoVazio(textosDosGatilhos.value));
const gatilhosRepetidos = computed(() =>
  listarRepetidos(textosDosGatilhos.value)
);

// O descarte de `linhasValidas` é correto (a API recusa nome vazio e repetido) mas era MUDO:
// a pessoa escolhia o time, esquecia o nome, via "Salvo" — e a linha continuava na tela,
// parecendo salva, enquanto a API nunca a recebeu. Mesma coisa ao renomear uma linha para um
// nome que já existe: vence a primeira, e o time escolhido na segunda evapora. O silêncio é
// que era o defeito, não a regra.
const temNomeVazio = computed(() =>
  categoriasLinhas.value.some(linha => !linha.nome.trim())
);
const nomesRepetidos = computed(() => {
  const vistos = new Set();
  const repetidos = new Set();
  categoriasLinhas.value.forEach(linha => {
    const nome = linha.nome.trim();
    if (!nome) return;
    if (vistos.has(nome)) repetidos.add(nome);
    else vistos.add(nome);
  });
  return [...repetidos];
});

// Um id guardado que não está na lista (a API de times falhou, ou o time foi apagado)
// continua aparecendo como opção, para o select nunca esvaziar em silêncio. Vale para os
// três lugares que escolhem time.
const idsEmUso = computed(() =>
  [
    roteamentoPadrao.value,
    escalonamentoPara.value,
    ...linhasValidas.value.map(linha => linha.time),
  ].filter(Boolean)
);
const teamOptions = computed(() => {
  const faltando = [...new Set(idsEmUso.value)].filter(
    id => !props.teams.some(team => String(team.id) === id)
  );
  return [...faltando.map(id => ({ id, name: `${id} (?)` })), ...props.teams];
});

// O que a API aceita para um time é inteiro ou nulo — nunca a string vazia do select.
function idOuNulo(valor) {
  const n = parseInt(valor, 10);
  return Number.isInteger(n) && n > 0 ? n : null;
}

// O que vai é o que fica no campo: minutos abaixo do piso sobem para ele à vista, em vez de
// um 422 em português no meio de uma tela em inglês. Os dois relógios desta tela — a
// dormência e o escalonamento — têm o mesmo piso e a mesma regra, e por isso a mesma conta:
// duas cópias dela divergiriam sem ninguém ver.
function minutosValidos(valor, padrao) {
  return Math.max(MINUTOS_MINIMOS, Math.round(Number(valor)) || padrao);
}

async function save() {
  // A API recusaria com um 422 que fala em caracteres de um campo que a tela nem mostra.
  if (quandoLongo.value) {
    useAlert(
      t('POWER_AGENTS.HANDOFF.WHEN_TOO_LONG', {
        total: quandoTexto.value.length,
        max: QUANDO_MAX,
      })
    );
    return;
  }
  const minutos = minutosValidos(
    escalonamentoMinutos.value,
    ESCALONAMENTO_PADRAO
  );
  escalonamentoMinutos.value = minutos;
  const minutosDormencia = minutosValidos(
    Number(dormenciaValor.value) * UNIDADES[dormenciaUnidade.value],
    DORMENCIA_PADRAO
  );
  // O campo volta normalizado a partir dos minutos que REALMENTE vão, e não do que foi
  // digitado: assim "3 horas" que o piso derrubou para 5 min aparece como "5 minutos", em
  // vez de virar 0,08 hora — um número que ninguém escreveu e que não quer dizer nada.
  dormenciaUnidade.value = unidadeDe(minutosDormencia);
  dormenciaValor.value = minutosDormencia / UNIDADES[dormenciaUnidade.value];
  // As rotas saem da MESMA lista que os nomes, então não existe rota para categoria que não
  // foi enviada — config que não faz nada, e que o runtime anota como tal.
  const rotas = {};
  linhasValidas.value.forEach(linha => {
    const id = idOuNulo(linha.time);
    if (id) rotas[linha.nome] = id;
  });
  // Lido no save e não no mount: a cópia canônica do agente muda a cada seção salva.
  const atual = props.agent.config?.handoff || {};

  saving.value = true;
  try {
    const updated = await powerAgentsApi.update(
      ctx.accountId(),
      ctx.agentId(),
      {
        config: {
          handoff: {
            ...atual,
            silenciar_quando_humano_responde: silenciarQuandoHumano.value,
            volta: {
              ...(atual.volta || {}),
              ao_resolver: voltaAoResolver.value,
              dormencia: {
                ativa: dormenciaAtiva.value,
                apos_minutos: minutosDormencia,
              },
            },
            // Sem `ativa`: o spread leva o que está gravado, e mudá-lo é do card.
            tool_solicitar_humano: {
              ...(atual.tool_solicitar_humano || {}),
              silenciar_ao_solicitar: silenciarAoSolicitar.value,
              abrir_conversa: abrirConversa.value,
              categorias: categorias.value,
              roteamento: {
                padrao: idOuNulo(roteamentoPadrao.value),
                por_categoria: rotas,
              },
              mensagem_ao_cliente: mensagemAoCliente.value.trim() || null,
              quando: quandoTexto.value,
              escalonamento: {
                ativo: escalonamentoAtivo.value,
                apos_minutos: minutos,
                para: idOuNulo(escalonamentoPara.value),
              },
              // O spread preserva o `modelo`, que esta tela não edita: escrever o bloco
              // inteiro sem ele apagaria a escolha de quem configurou por API.
              resumo_ao_encaminhar: {
                ...(atual.tool_solicitar_humano?.resumo_ao_encaminhar || {}),
                ativo: resumoAtivo.value,
                instrucao: resumoInstrucao.value.trim() || null,
              },
            },
          },
        },
      }
    );
    ctx.onSectionSaved(updated);
    // Handoff é comportamento do agente e só passa a valer quando a revisão é publicada.
    // Sem isto, quem salva acha que mudou — e o agente segue como antes até alguém publicar.
    ctx.markPending();
    useAlert(t('POWER_AGENTS.SAVED'));
    emit('saved');
  } catch (e) {
    useAlert(mensagemDoErro(e, t, t('POWER_AGENTS.SAVE_ERROR')));
  } finally {
    saving.value = false;
  }
}

// O botão de salvar é do diálogo, que é de quem abre: o formulário não sabe em que moldura está.
defineExpose({ save, saving });
</script>

<template>
  <fieldset
    :disabled="somenteLeitura"
    class="flex flex-col min-w-0 gap-5 p-0 m-0 border-0"
  >
    <!-- O estado da ferramenta, e não um controle: o switch mora no card. Desligada, o aviso
         diz o que acontece com o que está abaixo, em vez de esconder o formulário — dá para
         deixar tudo pronto antes de alguém ligar. -->
    <div class="flex flex-wrap items-center gap-2">
      <span
        class="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full"
        :class="
          ligada ? 'bg-n-teal-3 text-n-teal-11' : 'bg-n-alpha-2 text-n-slate-11'
        "
      >
        {{
          ligada
            ? t('POWER_AGENTS.HANDOFF.STATE_ON')
            : t('POWER_AGENTS.HANDOFF.STATE_OFF')
        }}
      </span>
      <span v-if="!ligada" class="text-xs text-n-slate-11">
        {{ t('POWER_AGENTS.HANDOFF.OFF_NOTE') }}
      </span>
    </div>

    <!-- Os gatilhos: uma situação por linha, no molde das categorias logo abaixo. Botões com
         `type="button"`, como os de lá: o diálogo é um <form>, e sem tipo eles salvariam. -->
    <div>
      <span class="block text-sm font-medium text-n-slate-12">
        {{ t('POWER_AGENTS.HANDOFF.WHEN') }}
      </span>
      <p class="mt-1 text-xs text-n-slate-10">
        {{ t('POWER_AGENTS.HANDOFF.WHEN_HINT') }}
      </p>

      <div v-if="gatilhosLinhas.length" class="flex flex-col gap-2 mt-2">
        <div
          v-for="(linha, indice) in gatilhosLinhas"
          :key="indice"
          class="flex items-center gap-2 p-2 border rounded-lg border-n-weak"
        >
          <input
            v-model="linha.texto"
            type="text"
            :maxlength="GATILHO_MAX"
            class="reset-base flex-1 min-w-0 px-2 py-1.5 text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand placeholder:text-n-slate-10"
            :placeholder="t('POWER_AGENTS.HANDOFF.WHEN_PLACEHOLDER')"
            :aria-label="t('POWER_AGENTS.HANDOFF.WHEN_ITEM')"
          />
          <Button
            type="button"
            variant="faded"
            color="slate"
            size="xs"
            icon="i-lucide-trash-2"
            class="shrink-0"
            :aria-label="t('POWER_AGENTS.HANDOFF.WHEN_REMOVE')"
            @click="removerGatilho(indice)"
          />
        </div>
      </div>

      <p v-if="gatilhoVazio" class="mt-2 text-xs text-n-amber-11">
        {{ t('POWER_AGENTS.HANDOFF.WHEN_EMPTY_WARNING') }}
      </p>
      <p v-if="gatilhosRepetidos.length" class="mt-2 text-xs text-n-amber-11">
        {{
          t('POWER_AGENTS.HANDOFF.WHEN_DUPLICATE_WARNING', {
            itens: gatilhosRepetidos.join(', '),
          })
        }}
      </p>
      <p v-if="quandoLongo" class="mt-2 text-xs text-n-ruby-11">
        {{
          t('POWER_AGENTS.HANDOFF.WHEN_TOO_LONG', {
            total: quandoTexto.length,
            max: QUANDO_MAX,
          })
        }}
      </p>

      <Button
        type="button"
        variant="faded"
        color="slate"
        size="xs"
        icon="i-lucide-plus"
        class="mt-2"
        :label="t('POWER_AGENTS.HANDOFF.WHEN_ADD')"
        @click="adicionarGatilho"
      />
    </div>

    <!-- Para onde vai: categoria e time na MESMA linha. Eram dois controles (um campo com
         vírgulas e uma lista de mapeamento embaixo), e a lista de baixo não dizia de onde os
         nomes vinham. Molde dos estágios do Follow-up: linhas com remover + adicionar. -->
    <section class="flex flex-col gap-3">
      <h4 class="text-sm font-semibold text-n-slate-12">
        {{ t('POWER_AGENTS.HANDOFF.GROUP_WHERE') }}
      </h4>

      <div>
        <span class="block text-sm font-medium text-n-slate-12">
          {{ t('POWER_AGENTS.HANDOFF.CATEGORIES') }}
        </span>
        <p class="mt-1 text-xs text-n-slate-10">
          {{ t('POWER_AGENTS.HANDOFF.CATEGORIES_HINT') }}
        </p>

        <div v-if="categoriasLinhas.length" class="flex flex-col gap-2 mt-2">
          <div
            v-for="(linha, indice) in categoriasLinhas"
            :key="indice"
            class="flex flex-wrap items-center gap-2 p-2 border rounded-lg border-n-weak sm:flex-nowrap"
          >
            <input
              v-model="linha.nome"
              type="text"
              class="reset-base flex-1 min-w-0 px-2 py-1.5 text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand placeholder:text-n-slate-10"
              :placeholder="t('POWER_AGENTS.HANDOFF.CATEGORY_NAME_PLACEHOLDER')"
            />
            <!-- `w-44` não é estética: a regra legada `select { width: 100% }` do
                 _base.scss não tem escape (a de input tem `.reset-base`), e sem uma
                 largura declarada este select esmagaria o campo ao lado até zero. -->
            <select
              v-model="linha.time"
              class="w-44 shrink-0 px-2 py-1.5 text-sm border rounded-lg outline-none border-n-weak bg-n-alpha-1 text-n-slate-12 focus:border-n-brand mb-0"
            >
              <option value="">
                {{ t('POWER_AGENTS.HANDOFF.ROUTING_USE_DEFAULT') }}
              </option>
              <option
                v-for="team in teamOptions"
                :key="team.id"
                :value="String(team.id)"
              >
                {{ team.name }}
              </option>
            </select>
            <Button
              type="button"
              variant="faded"
              color="slate"
              size="xs"
              icon="i-lucide-trash-2"
              class="shrink-0"
              :aria-label="t('POWER_AGENTS.HANDOFF.CATEGORY_REMOVE')"
              @click="removerCategoria(indice)"
            />
          </div>
        </div>

        <p v-if="temNomeVazio" class="mt-2 text-xs text-n-amber-11">
          {{ t('POWER_AGENTS.HANDOFF.CATEGORY_EMPTY_WARNING') }}
        </p>
        <p v-if="nomesRepetidos.length" class="mt-2 text-xs text-n-amber-11">
          {{
            t('POWER_AGENTS.HANDOFF.CATEGORY_DUPLICATE_WARNING', {
              nomes: nomesRepetidos.join(', '),
            })
          }}
        </p>

        <!-- `type="button"` nos dois botões da lista: o Dialog embrulha este formulário num
             <form @submit="confirm">, e botão sem tipo dentro de form é submit — adicionar
             uma categoria salvava o diálogo inteiro. -->
        <Button
          type="button"
          variant="faded"
          color="slate"
          size="xs"
          icon="i-lucide-plus"
          class="mt-2"
          :label="t('POWER_AGENTS.HANDOFF.CATEGORY_ADD')"
          @click="adicionarCategoria"
        />
      </div>

      <div>
        <label class="block mb-1 text-sm font-medium text-n-slate-12">
          {{ t('POWER_AGENTS.HANDOFF.ROUTING_DEFAULT') }}
        </label>
        <!-- O vazio diz para ONDE vai, e não "nenhum time": sem time, o pedido abre a
             conversa na fila geral da caixa — medido na Experience Club em 21/09. "Deixar
             sem atribuição" soava como encaminhar para lugar nenhum. -->
        <select
          v-model="roteamentoPadrao"
          class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12 outline-none focus:border-n-brand mb-0"
        >
          <option value="">
            {{ t('POWER_AGENTS.HANDOFF.ROUTING_NONE') }}
          </option>
          <option
            v-for="team in teamOptions"
            :key="team.id"
            :value="String(team.id)"
          >
            {{ team.name }}
          </option>
        </select>
        <p class="mt-1 text-xs text-n-slate-10">
          {{ t('POWER_AGENTS.HANDOFF.ROUTING_DEFAULT_HINT') }}
        </p>
      </div>
    </section>

    <section class="flex flex-col gap-3">
      <h4 class="text-sm font-semibold text-n-slate-12">
        {{ t('POWER_AGENTS.HANDOFF.GROUP_HANDING_OFF') }}
      </h4>

      <div>
        <div class="flex items-start gap-3">
          <Switch v-model="abrirConversa" class="mt-0.5" />
          <div class="min-w-0">
            <span class="block text-sm font-medium text-n-slate-12">
              {{ t('POWER_AGENTS.HANDOFF.TOOL_OPEN') }}
            </span>
            <span class="block mt-0.5 text-xs text-n-slate-10">
              {{ t('POWER_AGENTS.HANDOFF.TOOL_OPEN_HINT') }}
            </span>
          </div>
        </div>
        <!-- Desligar é uma escolha, e ela merece o aviso na hora — não a descoberta de
             um pedido de humano que ficou na fila de ninguém. -->
        <p v-if="!abrirConversa" class="mt-2 text-xs text-n-amber-11">
          {{ t('POWER_AGENTS.HANDOFF.TOOL_OPEN_OFF_WARNING') }}
        </p>
      </div>

      <div class="flex items-start gap-3">
        <Switch v-model="silenciarAoSolicitar" class="mt-0.5" />
        <div class="min-w-0">
          <span class="block text-sm font-medium text-n-slate-12">
            {{ t('POWER_AGENTS.HANDOFF.TOOL_SILENCE') }}
          </span>
          <span class="block mt-0.5 text-xs text-n-slate-10">
            {{ t('POWER_AGENTS.HANDOFF.TOOL_SILENCE_HINT') }}
          </span>
        </div>
      </div>

      <div>
        <label class="block mb-1 text-sm font-medium text-n-slate-12">
          {{ t('POWER_AGENTS.HANDOFF.MESSAGE_TO_CUSTOMER') }}
        </label>
        <textarea
          v-model="mensagemAoCliente"
          rows="3"
          class="w-full px-3 py-2 text-sm border rounded-lg outline-none resize-y border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand placeholder:text-n-slate-10"
          :placeholder="t('POWER_AGENTS.HANDOFF.MESSAGE_PLACEHOLDER')"
        />
        <p class="mt-1 text-xs text-n-slate-10">
          {{ t('POWER_AGENTS.HANDOFF.MESSAGE_TO_CUSTOMER_HINT') }}
        </p>
      </div>
    </section>

    <!-- O resumo do encaminhamento: uma anotação PRIVADA na conversa, para quem pegar não
         recomeçar lendo o histórico de trás para frente. É o que acontece quando o AGENTE
         pede humano; o encaminhamento pela tela não passa por aqui. -->
    <div class="p-3 border rounded-lg border-n-weak">
      <div class="flex items-start gap-3">
        <Switch v-model="resumoAtivo" class="mt-0.5" />
        <div class="min-w-0">
          <span class="block text-sm font-medium text-n-slate-12">
            {{ t('POWER_AGENTS.HANDOFF.SUMMARY') }}
          </span>
          <span class="block mt-0.5 text-xs text-n-slate-10">
            {{ t('POWER_AGENTS.HANDOFF.SUMMARY_HINT') }}
          </span>
        </div>
      </div>

      <div v-if="resumoAtivo" class="mt-3">
        <label class="block mb-1 text-sm font-medium text-n-slate-12">
          {{ t('POWER_AGENTS.HANDOFF.SUMMARY_INSTRUCTION') }}
        </label>
        <textarea
          v-model="resumoInstrucao"
          rows="2"
          class="w-full px-3 py-2 text-sm border rounded-lg outline-none resize-y border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand placeholder:text-n-slate-10"
          :placeholder="
            t('POWER_AGENTS.HANDOFF.SUMMARY_INSTRUCTION_PLACEHOLDER')
          "
        />
        <p class="mt-1 text-xs text-n-slate-10">
          {{ t('POWER_AGENTS.HANDOFF.SUMMARY_INSTRUCTION_HINT') }}
        </p>
      </div>
    </div>

    <!-- Escalonamento: o pedido que ninguém pegou vai para outro time. -->
    <div class="p-3 border rounded-lg border-n-weak">
      <div class="flex items-start gap-3">
        <Switch v-model="escalonamentoAtivo" class="mt-0.5" />
        <div class="min-w-0">
          <span class="block text-sm font-medium text-n-slate-12">
            {{ t('POWER_AGENTS.HANDOFF.ESCALATION') }}
          </span>
          <span class="block mt-0.5 text-xs text-n-slate-10">
            {{ t('POWER_AGENTS.HANDOFF.ESCALATION_HINT') }}
          </span>
        </div>
      </div>

      <div
        v-if="escalonamentoAtivo"
        class="flex flex-wrap items-end gap-3 mt-3"
      >
        <label class="flex items-center gap-2 text-xs text-n-slate-11">
          {{ t('POWER_AGENTS.HANDOFF.ESCALATION_AFTER') }}
          <input
            v-model="escalonamentoMinutos"
            type="number"
            :min="MINUTOS_MINIMOS"
            step="1"
            class="reset-base w-24 px-2 py-1.5 text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand"
          />
          {{
            t('POWER_AGENTS.HANDOFF.ESCALATION_MINUTES', {
              min: MINUTOS_MINIMOS,
            })
          }}
        </label>
        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          {{ t('POWER_AGENTS.HANDOFF.ESCALATION_TO') }}
          <select
            v-model="escalonamentoPara"
            class="w-48 px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12 outline-none focus:border-n-brand mb-0"
          >
            <option value="">
              {{ t('POWER_AGENTS.HANDOFF.NO_TEAM') }}
            </option>
            <option
              v-for="team in teamOptions"
              :key="team.id"
              :value="String(team.id)"
            >
              {{ team.name }}
            </option>
          </select>
        </label>
      </div>

      <!-- Escalar para ninguém é a config prometendo o que o worker não faz: o runtime
           desliga o escalonamento por conta própria e anota. Melhor dizer aqui. -->
      <p
        v-if="escalonamentoAtivo && !escalonamentoPara"
        class="mt-2 text-xs text-n-amber-11"
      >
        {{ t('POWER_AGENTS.HANDOFF.ESCALATION_NO_TEAM_WARNING') }}
      </p>
    </div>

    <!-- Quando uma pessoa entra na conversa: vale com a ferramenta DESLIGADA, e por isso o
         grupo diz isso por escrito. Calar quando o atendente responde e voltar quando a
         conversa é resolvida não dependem de o agente ter pedido alguém. -->
    <section class="flex flex-col gap-3 p-3 rounded-lg bg-n-alpha-1">
      <div>
        <h4 class="text-sm font-semibold text-n-slate-12">
          {{ t('POWER_AGENTS.HANDOFF.GROUP_PRESENCE') }}
        </h4>
        <p class="mt-0.5 text-xs text-n-amber-11">
          {{ t('POWER_AGENTS.HANDOFF.GROUP_PRESENCE_NOTE') }}
        </p>
      </div>

      <div class="flex items-start gap-3">
        <Switch v-model="silenciarQuandoHumano" class="mt-0.5" />
        <div class="min-w-0">
          <span class="block text-sm font-medium text-n-slate-12">
            {{ t('POWER_AGENTS.HANDOFF.SILENCE_ON_HUMAN') }}
          </span>
          <span class="block mt-0.5 text-xs text-n-slate-10">
            {{ t('POWER_AGENTS.HANDOFF.SILENCE_ON_HUMAN_HINT') }}
          </span>
        </div>
      </div>

      <div class="flex items-start gap-3">
        <Switch v-model="voltaAoResolver" class="mt-0.5" />
        <div class="min-w-0">
          <span class="block text-sm font-medium text-n-slate-12">
            {{ t('POWER_AGENTS.HANDOFF.RESUME_ON_RESOLVE') }}
          </span>
          <span class="block mt-0.5 text-xs text-n-slate-10">
            {{ t('POWER_AGENTS.HANDOFF.RESUME_ON_RESOLVE_HINT') }}
          </span>
        </div>
      </div>

      <!-- A outra metade do "quando volta": a pausa por humano vence sozinha de qualquer
           jeito, nas 24 h de sempre. Ligar a dormência é tirar essas 24 h do código e pôr na
           mão de quem opera. Vale para a pausa que o atendente causou ao responder — e só
           para ela. -->
      <div class="p-3 border rounded-lg border-n-weak bg-n-background">
        <div class="flex items-start gap-3">
          <Switch v-model="dormenciaAtiva" class="mt-0.5" />
          <div class="min-w-0">
            <span class="block text-sm font-medium text-n-slate-12">
              {{ t('POWER_AGENTS.HANDOFF.DORMANCY') }}
            </span>
            <span class="block mt-0.5 text-xs text-n-slate-10">
              {{ t('POWER_AGENTS.HANDOFF.DORMANCY_HINT') }}
            </span>
          </div>
        </div>

        <div
          v-if="dormenciaAtiva"
          class="flex flex-wrap items-center gap-2 mt-3"
        >
          <label
            class="flex flex-wrap items-center gap-2 text-xs text-n-slate-11"
          >
            {{ t('POWER_AGENTS.HANDOFF.DORMANCY_AFTER') }}
            <input
              v-model="dormenciaValor"
              type="number"
              :min="dormenciaUnidade === 'minutos' ? MINUTOS_MINIMOS : 1"
              step="1"
              class="reset-base w-20 px-2 py-1.5 text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand"
            />
            <select
              v-model="dormenciaUnidade"
              class="w-28 shrink-0 px-2 py-1.5 text-xs border rounded-lg outline-none border-n-weak bg-n-alpha-1 text-n-slate-12 focus:border-n-brand mb-0"
            >
              <option value="minutos">
                {{ t('POWER_AGENTS.HANDOFF.DORMANCY_UNIT_MINUTES') }}
              </option>
              <option value="horas">
                {{ t('POWER_AGENTS.HANDOFF.DORMANCY_UNIT_HOURS') }}
              </option>
              <option value="dias">
                {{ t('POWER_AGENTS.HANDOFF.DORMANCY_UNIT_DAYS') }}
              </option>
            </select>
            {{
              t('POWER_AGENTS.HANDOFF.DORMANCY_SINCE', { min: MINUTOS_MINIMOS })
            }}
          </label>
        </div>
      </div>
    </section>
  </fieldset>
</template>
